import { router } from "@inertiajs/react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  attach_docx_bizminer_admin_organization_account_pillar_insight_path,
  remove_docx_bizminer_admin_organization_account_pillar_insight_path,
} from "@/routes";
import type { BizminerPillarInsight } from "@/types/serializers";

interface Props {
  pillarInsight: BizminerPillarInsight;
  organizationId: number;
  accountId: number;
  trigger: React.ReactNode;
}

export default function DocxUploadDialog({
  pillarInsight,
  organizationId,
  accountId,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files?.[0]) {
      setSelectedFile(files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) return;

    setUploading(true);

    const formData = new FormData();
    formData.append("docx_file", selectedFile);

    router.post(
      attach_docx_bizminer_admin_organization_account_pillar_insight_path(
        organizationId,
        accountId,
        pillarInsight.id,
      ),
      formData,
      {
        onFinish: () => {
          setUploading(false);
          setSelectedFile(null);
          setOpen(false);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!confirm("Are you sure you want to remove the DOCX report?")) return;

    router.delete(
      remove_docx_bizminer_admin_organization_account_pillar_insight_path(
        organizationId,
        accountId,
        pillarInsight.id,
      ),
      {
        preserveState: true,
        preserveScroll: true,
      },
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload DOCX Report</DialogTitle>
          <DialogDescription>
            Attach a DOCX report file to {pillarInsight.title}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {pillarInsight.docx_report_file ? (
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <div className="text-sm font-medium text-card-foreground">
                      {pillarInsight.docx_report_file.filename}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatFileSize(pillarInsight.docx_report_file.size)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={pillarInsight.docx_report_file.url}
                    className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-primary hover:text-primary/80"
                  >
                    <Download className="h-3 w-3" />
                    <span>Download</span>
                  </a>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="h-3 w-3" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <label
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`flex flex-col items-center px-4 py-6 bg-background border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary"
                }`}
              >
                <Upload
                  className={`h-10 w-10 ${isDragging ? "text-primary" : "text-muted-foreground"}`}
                />
                <span className="mt-2 text-sm text-muted-foreground text-center">
                  {selectedFile
                    ? selectedFile.name
                    : isDragging
                      ? "Drop file here"
                      : "Click to select or drag & drop a DOCX file"}
                </span>
                <input
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>

              {selectedFile && (
                <div className="mt-3 flex items-center space-x-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}{" "}
                    KB)
                  </span>
                </div>
              )}
            </>
          )}

          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm font-medium text-card-foreground bg-background border border-input rounded-md hover:bg-muted"
            >
              Close
            </button>
            {!pillarInsight.docx_report_file && (
              <button
                type="submit"
                disabled={uploading || !selectedFile}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
